import React, { useEffect, useState, useRef } from "react";
import { useTranslation } from "react-i18next";
// Times in the business's own zone and the screen's language (src/utils/times.js, 4.3d.4).
import { dateText, dayText, timeText, todayDay, daysAgoDay } from "../../../utils/times";
import {
  Box, Typography, Chip, Avatar, Skeleton, Button,
  Popover, Dialog, DialogTitle, DialogContent, DialogActions,
  Switch, FormControlLabel, TextField, Divider, IconButton, Tooltip,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import { useSelector, useDispatch } from "react-redux";
import {
  Phone, PhoneCall, Users, UserCheck, Ticket,
  TrendingUp, TrendingDown, CalendarDays, Plus,
  MoreHorizontal, Brain, Mic, ChevronDown, X, LayoutDashboard,
} from "lucide-react";
import { fetchSummary } from "../analytics/analyticsSlice";
import { fetchUrgentTickets, fetchOpenCount } from "../crm/crmSlice";
import axiosInstance from "../../../services/axiosInstance";
// The weekly digest is ready (4.6c): a line at the top while the newest is unread.
import DigestBanner from "../analytics/insights/DigestBanner";

// ── Date helpers ─────────────────────────────────────────────────
// The business's days, not UTC's (between midnight and 03:00 in Amman, UTC's "today" was yesterday).
const todayISO  = () => todayDay();
const daysAgoISO = (n) => daysAgoDay(n);
// This week's Monday and this month's first, as the business's days.
const weekStartISO = () => {
  const today = todayDay();
  const day = new Date(`${today}T12:00:00Z`).getUTCDay(); // 0=Sun
  return daysAgoDay(day === 0 ? 6 : day - 1);
};
const monthStartISO = () => `${todayDay().slice(0, 7)}-01`;

const PRESETS = [
  { id: "today",  label: "Today",        from: todayISO,     to: todayISO },
  { id: "week",   label: "This Week",    from: weekStartISO, to: todayISO },
  { id: "7d",     label: "Last 7 Days",  from: () => daysAgoISO(6), to: todayISO },
  { id: "month",  label: "This Month",   from: monthStartISO, to: todayISO },
  { id: "30d",    label: "Last 30 Days", from: () => daysAgoISO(29), to: todayISO },
  { id: "custom", label: "Custom range", from: null, to: null },
];

const fmtPresetLabel = (preset, from, to, t) => {
  if (preset !== "custom") {
    if (t) {
      const map = { today: t("presetToday"), week: t("presetThisWeek"), "7d": t("presetLast7"), month: t("presetThisMonth"), "30d": t("presetLast30") };
      return map[preset] || PRESETS.find(p => p.id === preset)?.label || preset;
    }
    return PRESETS.find(p => p.id === preset)?.label || preset;
  }
  const fmt = (s) => s ? dayText(s, undefined, { month: "short", day: "numeric" }) : "";
  return `${fmt(from)} – ${fmt(to)}`;
};

// ── Default widget visibility ────────────────────────────────────
const DEFAULT_WIDGETS = {
  kpis:           true,
  callActivity:   true,
  topDisposition: true,
  aiInsights:     true,
  recentCalls:    true,
  tickets:        true,
  activities:     true,
};
const WIDGET_LABELS = {
  kpis:           "KPI Cards",
  callActivity:   "Call Activity Chart",
  topDisposition: "Top Call Disposition",
  aiInsights:     "AI Insights",
  recentCalls:    "Recent Calls",
  tickets:        "Follow-up Tickets",
  activities:     "Recent Activities",
};

function loadWidgets() {
  try { return JSON.parse(localStorage.getItem("dash_widgets") || "null") || DEFAULT_WIDGETS; }
  catch { return DEFAULT_WIDGETS; }
}

function loadDateState() {
  try {
    const s = JSON.parse(localStorage.getItem("dash_date") || "null");
    if (s?.preset && s?.dateFrom && s?.dateTo) {
      // Re-evaluate date bounds for named presets so "Today" always means today,
      // "This Week" always means the current week, etc. — even after overnight sessions.
      if (s.preset !== "custom") {
        const p = PRESETS.find((p) => p.id === s.preset);
        if (p) return { ...s, dateFrom: p.from(), dateTo: p.to() };
      }
      return s;
    }
  } catch {}
  const today = todayISO();
  return { preset: "today", dateFrom: today, dateTo: today, customFrom: today, customTo: today };
}

// ── SVG chart helpers ────────────────────────────────────────────
function buildPts(data, key, w, h, pad = 10) {
  if (!data?.length) return "";
  const vals = data.map((d) => Number(d[key]) || 0);
  const max = Math.max(...vals, 1);
  return data
    .map((d, i) => {
      const x = (i / Math.max(data.length - 1, 1)) * w;
      const y = h - pad - ((Number(d[key]) || 0) / max) * (h - pad * 2);
      return `${x.toFixed(1)},${y.toFixed(1)}`;
    })
    .join(" ");
}

function buildArea(data, key, w, h, pad = 10) {
  const pts = buildPts(data, key, w, h, pad);
  if (!pts) return "";
  const arr = pts.split(" ");
  return `M ${arr.join(" L ")} L ${w},${h} L 0,${h} Z`;
}

function DonutChart({ segments, size = 120, thickness = 16 }) {
  const r = (size - thickness) / 2 - 1;
  const circ = 2 * Math.PI * r;
  const cx = size / 2, cy = size / 2;
  const total = segments.reduce((s, g) => s + (g.value || 0), 0) || 1;
  const GAP_ARC = (3 / 360) * circ;
  let cumOff = 0;

  return (
    <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
      <circle cx={cx} cy={cy} r={r} fill="none" stroke="var(--line-100)" strokeWidth={thickness} />
      {segments.map((seg, i) => {
        if (!seg.value) return null;
        const arc = Math.max(((seg.value / total) * circ) - GAP_ARC, 0);
        const dashOff = circ - cumOff;
        cumOff += arc + GAP_ARC;
        return (
          <circle key={i} cx={cx} cy={cy} r={r} fill="none"
            stroke={seg.color} strokeWidth={thickness}
            strokeDasharray={`${arc.toFixed(2)} ${(circ - arc).toFixed(2)}`}
            strokeDashoffset={dashOff.toFixed(2)}
            transform={`rotate(-90 ${cx} ${cy})`}
            strokeLinecap="butt"
          />
        );
      })}
    </svg>
  );
}

// ── Shared card wrapper ──────────────────────────────────────────
function Card({ children, title, action, sx }) {
  return (
    <Box sx={{
      borderRadius: "var(--r-lg)",
      background: "var(--surface-card)",
      border: "1px solid var(--line-100)",
      boxShadow: "var(--shadow-card)",
      overflow: "hidden",
      ...sx,
    }}>
      {(title || action) && (
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", px: "20px", pt: "18px", pb: "12px" }}>
          <Typography sx={{ fontSize: "15px", fontWeight: 700, color: "var(--ink-900)", fontFamily: "var(--font-display)" }}>
            {title}
          </Typography>
          {action}
        </Box>
      )}
      {children}
    </Box>
  );
}

// ── KPI card ─────────────────────────────────────────────────────
function KpiCard({ label, value, IconComp, iconBg, iconColor, delta, up, loading }) {
  const { t } = useTranslation("overview");
  return (
    <Box sx={{
      flex: "1 1 0", minWidth: 0,
      p: "18px 20px",
      borderRadius: "var(--r-lg)",
      background: "var(--surface-card)",
      border: "1px solid var(--line-100)",
      boxShadow: "var(--shadow-card)",
    }}>
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", mb: "12px" }}>
        <Typography sx={{ fontSize: "13px", fontWeight: 500, color: "var(--ink-500)", fontFamily: "var(--font-ui)" }}>
          {label}
        </Typography>
        <Box sx={{ width: 36, height: 36, borderRadius: "50%", background: iconBg, display: "grid", placeItems: "center", flexShrink: 0 }}>
          <IconComp size={17} color={iconColor} />
        </Box>
      </Box>

      {loading ? (
        <Skeleton width={80} height={40} sx={{ mb: "4px" }} />
      ) : (
        <Typography sx={{
          fontSize: "var(--t-kpi)", fontWeight: 800, letterSpacing: "-0.03em",
          lineHeight: 1.1, color: "var(--ink-900)",
          fontFamily: "var(--font-display)", mb: "6px",
          fontVariantNumeric: "tabular-nums",
        }}>
          {value?.toLocaleString() ?? "—"}
        </Typography>
      )}

      {delta && (
        <Box sx={{ display: "flex", alignItems: "center", gap: "4px" }}>
          {up
            ? <TrendingUp size={12} color="var(--success)" />
            : <TrendingDown size={12} color="var(--danger)" />}
          <Typography sx={{ fontSize: "12px", fontWeight: 600, color: up ? "var(--success)" : "var(--danger)", fontFamily: "var(--font-ui)" }}>
            {delta}
          </Typography>
          <Typography sx={{ fontSize: "12px", color: "var(--ink-400)", fontFamily: "var(--font-ui)" }}>
            {t("vsLastPeriod")}
          </Typography>
        </Box>
      )}
    </Box>
  );
}

// ── Call Activity chart ──────────────────────────────────────────
function CallActivityChart({ trend }) {
  const { t } = useTranslation("overview");
  const W = 420, H = 128;
  const data = trend || [];

  if (!data.length) {
    return (
      <Box sx={{ height: 128, display: "grid", placeItems: "center", pb: "16px" }}>
        <Typography sx={{ fontSize: "13px", color: "var(--ink-400)", fontFamily: "var(--font-ui)" }}>{t("noDataPeriod")}</Typography>
      </Box>
    );
  }

  const totalPts  = buildPts(data, "total",    W, H);
  const answPts   = buildPts(data, "answered", W, H);
  const totalArea = buildArea(data, "total",    W, H);
  const answArea  = buildArea(data, "answered", W, H);

  return (
    <Box sx={{ px: "20px", pb: "14px" }}>
      <svg viewBox={`0 0 ${W} ${H}`} width="100%" style={{ overflow: "visible", display: "block" }}>
        <defs>
          <linearGradient id="gIn" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#1FC0DA" stopOpacity="0.18" />
            <stop offset="100%" stopColor="#1FC0DA" stopOpacity="0" />
          </linearGradient>
          <linearGradient id="gOut" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#7A3FE4" stopOpacity="0.13" />
            <stop offset="100%" stopColor="#7A3FE4" stopOpacity="0" />
          </linearGradient>
        </defs>
        {totalArea  && <path d={totalArea}  fill="url(#gIn)"  />}
        {answArea   && <path d={answArea}   fill="url(#gOut)" />}
        {totalPts   && <polyline points={totalPts}  fill="none" stroke="#1FC0DA" strokeWidth="2.5" strokeLinejoin="round" strokeLinecap="round" />}
        {answPts    && <polyline points={answPts}   fill="none" stroke="#7A3FE4" strokeWidth="2"   strokeLinejoin="round" strokeLinecap="round" />}
      </svg>

      <Box sx={{ display: "flex", justifyContent: "space-between", mt: "4px" }}>
        {data.map((d, i) => (
          <Typography key={i} sx={{ fontSize: "10px", color: "var(--ink-400)", fontFamily: "var(--font-ui)" }}>
            {d.date ? dayText(d.date, undefined, { month: "short", day: "numeric" }) : ""}
          </Typography>
        ))}
      </Box>

      <Box sx={{ display: "flex", gap: "20px", mt: "8px" }}>
        {[{ color: "#1FC0DA", label: t("legendTotal") }, { color: "#7A3FE4", label: t("legendAnswered") }].map(({ color, label }) => (
          <Box key={label} sx={{ display: "flex", alignItems: "center", gap: "6px" }}>
            <Box sx={{ width: 22, height: 2.5, borderRadius: 1, background: color }} />
            <Typography sx={{ fontSize: "11px", color: "var(--ink-400)", fontFamily: "var(--font-ui)" }}>{label}</Typography>
          </Box>
        ))}
      </Box>
    </Box>
  );
}

// ── Preset dropdown (shared by header date pill and chart header) ─
function PresetMenu({ anchorEl, onClose, preset, onPreset, customFrom, customTo, onCustomFrom, onCustomTo }) {
  const { t } = useTranslation("overview");
  const open = Boolean(anchorEl);
  const presetLabelMap = { today: t("presetToday"), week: t("presetThisWeek"), "7d": t("presetLast7"), month: t("presetThisMonth"), "30d": t("presetLast30") };

  const handleClose = () => {
    if (document.activeElement instanceof HTMLElement) document.activeElement.blur();
    onClose();
  };

  return (
    <Popover open={open} anchorEl={anchorEl} onClose={handleClose}
      disableAutoFocus
      anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      transformOrigin={{ vertical: "top", horizontal: "right" }}
      PaperProps={{ sx: { borderRadius: "12px", boxShadow: "0 8px 32px rgba(0,0,0,.14)", mt: "6px", minWidth: 220, overflow: "hidden" } }}>
      <Box sx={{ p: "8px" }}>
        {PRESETS.filter(p => p.id !== "custom").map(p => (
          <Box key={p.id} onClick={() => { onPreset(p.id); handleClose(); }}
            sx={{
              px: "12px", py: "8px", borderRadius: "8px", cursor: "pointer",
              fontFamily: "var(--font-ui)", fontSize: "13px", fontWeight: preset === p.id ? 700 : 500,
              color: preset === p.id ? "var(--brand-violet)" : "var(--ink-700)",
              background: preset === p.id ? alpha("#7A3FE4", 0.08) : "transparent",
              "&:hover": { background: alpha("#7A3FE4", 0.06) },
              transition: "background 0.12s",
            }}>
            {presetLabelMap[p.id] || p.label}
          </Box>
        ))}
        <Divider sx={{ my: "6px" }} />
        <Box sx={{ px: "12px", py: "6px" }}>
          <Typography sx={{ fontSize: "11px", fontWeight: 600, color: "var(--ink-400)", fontFamily: "var(--font-ui)", mb: "6px", textTransform: "uppercase", letterSpacing: ".06em" }}>
            {t("customRange")}
          </Typography>
          <Box sx={{ display: "flex", flexDirection: "column", gap: "6px" }}>
            <TextField type="date" size="small" label={t("from", { ns: "callHistory" })} value={customFrom} fullWidth
              onChange={e => onCustomFrom(e.target.value)}
              inputProps={{ max: todayISO() }}
              InputLabelProps={{ shrink: true }}
              sx={{ "& .MuiInputBase-input": { fontSize: "13px" } }}
            />
            <TextField type="date" size="small" label={t("to", { ns: "callHistory" })} value={customTo} fullWidth
              onChange={e => onCustomTo(e.target.value)}
              inputProps={{ min: customFrom, max: todayISO() }}
              InputLabelProps={{ shrink: true }}
              sx={{ "& .MuiInputBase-input": { fontSize: "13px" } }}
            />
            <Button size="small" variant="contained" fullWidth
              disabled={!customFrom || !customTo}
              onClick={() => { onPreset("custom"); handleClose(); }}
              sx={{ background: "var(--brand-violet)", fontFamily: "var(--font-ui)", fontWeight: 600, fontSize: "12px", textTransform: "none", borderRadius: "8px" }}>
              {t("apply", { ns: "common" })}
            </Button>
          </Box>
        </Box>
      </Box>
    </Popover>
  );
}

// ── Call status display config ───────────────────────────────────
const CALL_STATUS_CFG = {
  ANSWER:      { tkey: "callStatusAnswered",    color: "#1FA971" },
  ANSWERED:    { tkey: "callStatusAnswered",    color: "#1FA971" },
  NOANSWER:    { tkey: "callStatusMissed",      color: "#E5484D" },
  BUSY:        { tkey: "callStatusBusy",        color: "#F59E0B" },
  CANCEL:      { tkey: "callStatusCanceled",    color: "#94A3B8" },
  CHANUNAVAIL: { tkey: "callStatusUnavailable", color: "#94A3B8" },
};

const fmtTime = (raw) => {
  if (!raw) return "—";
  const d = new Date(raw);
  const date = dateText(d, undefined, { month: "short", day: "numeric" });
  const time = timeText(d);
  return `${date} · ${time}`;
};

const fmtDuration = (sec) => {
  if (!sec) return "—";
  const m = Math.floor(sec / 60), s = sec % 60;
  return `${m}:${String(s).padStart(2, "0")}`;
};

// ── Main Overview component ──────────────────────────────────────
export default function Overview({ onNavigate }) {
  const { t } = useTranslation("overview");
  const dispatch = useDispatch();
  const user        = useSelector((s) => s.auth.user);
  const userDetails = useSelector((s) => s.auth.userDetails);
  const summary     = useSelector((s) => s.analytics.summary);
  const summaryStatus = useSelector((s) => s.analytics.summaryStatus);
  const openCount   = useSelector((s) => s.crm.openCount);
  const rawTickets  = useSelector((s) => s.crm.urgentTickets);
  const loading     = summaryStatus === "loading" || summaryStatus === "idle";

  const [recentCalls, setRecentCalls]   = useState([]);
  const [callsLoading, setCallsLoading] = useState(true);

  // ── Date range state (persisted in localStorage) ─────────────
  const [preset,     setPreset]     = useState(() => loadDateState().preset);
  const [dateFrom,   setDateFrom]   = useState(() => loadDateState().dateFrom);
  const [dateTo,     setDateTo]     = useState(() => loadDateState().dateTo);
  const [customFrom, setCustomFrom] = useState(() => loadDateState().customFrom);
  const [customTo,   setCustomTo]   = useState(() => loadDateState().customTo);
  const [dateAnchor, setDateAnchor] = useState(null);
  const [chartAnchor, setChartAnchor] = useState(null);

  // ── Period ticket count (any status, respects date range) ─────
  const [periodTickets, setPeriodTickets] = useState({ count: null, loading: true });

  // ── Widget visibility state ──────────────────────────────────
  const [widgetOpen, setWidgetOpen] = useState(false);
  const [widgets, setWidgets] = useState(loadWidgets);

  const applyPreset = (id) => {
    setPreset(id);
    if (id === "custom") {
      setDateFrom(customFrom);
      setDateTo(customTo);
    } else {
      const p = PRESETS.find(p => p.id === id);
      if (p) { setDateFrom(p.from()); setDateTo(p.to()); }
    }
  };

  // Persist date selection so refresh/remount doesn't reset it
  useEffect(() => {
    try { localStorage.setItem("dash_date", JSON.stringify({ preset, dateFrom, dateTo, customFrom, customTo })); }
    catch {}
  }, [preset, dateFrom, dateTo, customFrom, customTo]);

  // Re-fetch summary + period ticket count whenever date range changes
  useEffect(() => {
    if (!dateFrom || !dateTo) return;
    dispatch(fetchSummary({ dateFrom, dateTo }));
    setPeriodTickets({ count: null, loading: true });
    axiosInstance
      .get("/crm/tickets", { params: { dateFrom, dateTo, limit: 1 } })
      .then((r) => setPeriodTickets({ count: r.data.total ?? 0, loading: false }))
      .catch(() => setPeriodTickets({ count: 0, loading: false }));
  }, [dateFrom, dateTo, dispatch]);

  // Open count + urgent list — always current, not date-filtered
  useEffect(() => {
    dispatch(fetchUrgentTickets());
    dispatch(fetchOpenCount());
  }, [dispatch]);

  useEffect(() => {
    let mounted = true;
    axiosInstance
      .get("/Analytics/calls?page=1&size=5")
      .then((r) => { if (mounted) { setRecentCalls(r.data.items || []); setCallsLoading(false); } })
      .catch(() => { if (mounted) setCallsLoading(false); });
    return () => { mounted = false; };
  }, []);

  const toggleWidget = (key) => {
    setWidgets((prev) => {
      const next = { ...prev, [key]: !prev[key] };
      try { localStorage.setItem("dash_widgets", JSON.stringify(next)); } catch {}
      return next;
    });
  };

  const firstName = userDetails?.firstName || user?.name?.split(" ")[0] || "there";
  const hour      = new Date().getHours();
  const greeting  = hour < 12 ? t("goodMorning") : hour < 18 ? t("goodAfternoon") : t("goodEvening");

  const total     = summary?.total      || 0;
  const answered  = summary?.answered   || 0;
  const busy      = summary?.busy       || 0;
  const missed    = summary?.missed     || 0; // NOANSWER + BUSY
  const noAnswer  = Math.max(0, missed - busy);
  const voicemail = summary?.voicemails || 0;

  const donutSegs = [
    { label: t("dispositionAnswered", { defaultValue: "Answered" }),  value: answered,  color: "#1FC0DA" },
    { label: t("dispositionNoAnswer", { defaultValue: "No Answer" }), value: noAnswer,  color: "#7A3FE4" },
    { label: t("dispositionBusy",     { defaultValue: "Busy" }),      value: busy,      color: "#F59E0B" },
    { label: t("dispositionVoicemail",{ defaultValue: "Voicemail" }), value: voicemail, color: "#3B7DF0" },
  ];

  return (
    <Box sx={{ maxWidth: 1360, mx: "auto", position: "relative" }}>
      <DigestBanner onNavigate={onNavigate} />

      {/* ── Header row ─────────────────────────────────────── */}
      <Box sx={{ display: "flex", alignItems: { xs: "flex-start", sm: "center" }, justifyContent: "space-between", flexWrap: "wrap", gap: 2, mb: "28px" }}>
        <Box>
          <Typography sx={{ fontSize: { xs: "22px", md: "26px" }, fontWeight: 800, color: "var(--ink-900)", letterSpacing: "-0.02em", fontFamily: "var(--font-display)", lineHeight: 1.15 }}>
            {greeting}, {firstName} 👋
          </Typography>
          <Typography sx={{ fontSize: "14px", color: "var(--ink-500)", mt: "4px", fontFamily: "var(--font-ui)" }}>
            {t("subtitle")}
          </Typography>
        </Box>

        <Box sx={{ display: "flex", alignItems: "center", gap: "12px", flexShrink: 0, flexWrap: "wrap" }}>
          {/* Date range pill */}
          <Box onClick={(e) => setDateAnchor(e.currentTarget)}
            sx={{
              display: "flex", alignItems: "center", gap: "8px",
              px: "14px", py: "8px", borderRadius: "10px",
              border: "1px solid var(--line-200)", background: "var(--surface-card)",
              cursor: "pointer", userSelect: "none",
              "&:hover": { borderColor: "var(--brand-violet)" },
              transition: "border-color 0.15s",
            }}>
            <CalendarDays size={14} color="var(--ink-500)" />
            <Typography sx={{ fontSize: "13px", fontWeight: 500, color: "var(--ink-700)", fontFamily: "var(--font-ui)", fontVariantNumeric: "tabular-nums" }}>
              {fmtPresetLabel(preset, dateFrom, dateTo, t)}
            </Typography>
            <ChevronDown size={13} color="var(--ink-400)" />
          </Box>

          {/* Add Widget */}
          <Button startIcon={<Plus size={15} />} onClick={() => setWidgetOpen(true)}
            sx={{
              background: "linear-gradient(135deg,#1FC0DA,#4B78EC 50%,#7A3FE4)",
              color: "#fff", fontFamily: "var(--font-ui)", fontWeight: 600,
              fontSize: "13px", textTransform: "none",
              borderRadius: "10px", px: "16px", py: "7px",
              boxShadow: "var(--shadow-cta)",
              "&:hover": { filter: "brightness(1.06)", boxShadow: "0 8px 20px rgba(90,70,220,.30)" },
            }}>
            {t("addWidget")}
          </Button>
        </Box>
      </Box>

      {/* ── KPI row ─────────────────────────────────────────── */}
      {widgets.kpis && (
        <Box sx={{ display: "flex", gap: "14px", mb: "18px", flexWrap: { xs: "wrap", lg: "nowrap" } }}>
          <KpiCard label={t("kpiTotalCalls")}   value={summary?.total}       IconComp={Phone}     iconBg="rgba(31,192,218,.12)"  iconColor="#1FC0DA" delta={summary?.prev ? `${Math.abs(Math.round((total - summary.prev.total) / (summary.prev.total || 1) * 100))}%` : null} up={(summary?.total||0) >= (summary?.prev?.total||0)} loading={loading} />
          <KpiCard label={t("kpiAnswered")}     value={summary?.answered}    IconComp={PhoneCall} iconBg="rgba(31,192,218,.12)"  iconColor="#1FC0DA" delta={summary?.prev ? `${Math.abs(Math.round((answered - summary.prev.answered) / (summary.prev.answered || 1) * 100))}%` : null} up={(summary?.answered||0) >= (summary?.prev?.answered||0)} loading={loading} />
          <KpiCard label={t("kpiTotalAgents")}  value={summary?.totalAgents} IconComp={Users}     iconBg="rgba(122,63,228,.12)"  iconColor="#7A3FE4" loading={loading} />
          <KpiCard label={t("kpiAnswerRate")}   value={summary?.answerRate != null ? `${summary.answerRate}%` : null} IconComp={UserCheck} iconBg="rgba(59,125,240,.12)" iconColor="#3B7DF0" loading={loading} />
          <KpiCard label={t("kpiTicketsOpen")}  value={openCount ?? 0}       IconComp={Ticket}    iconBg="rgba(245,158,11,.12)"  iconColor="#F59E0B" loading={false} />
        </Box>
      )}

      {/* ── Middle row ──────────────────────────────────────── */}
      <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: `${widgets.callActivity && (widgets.topDisposition || widgets.aiInsights) ? "1.8fr 1.2fr 1fr" : "1fr"}` }, gap: "14px", mb: "14px" }}>

        {widgets.callActivity && (
          <Card title={t("cardCallActivity")} action={
            <Box onClick={(e) => setChartAnchor(e.currentTarget)}
              sx={{
                display: "flex", alignItems: "center", gap: "5px",
                px: "12px", py: "5px", borderRadius: "8px",
                border: "1px solid var(--line-200)", cursor: "pointer", userSelect: "none",
                "&:hover": { borderColor: "var(--brand-violet)" },
                transition: "border-color 0.15s",
              }}>
              <Typography sx={{ fontSize: "12px", fontWeight: 500, color: "var(--ink-600)", fontFamily: "var(--font-ui)" }}>
                {fmtPresetLabel(preset, dateFrom, dateTo, t)}
              </Typography>
              <ChevronDown size={12} color="var(--ink-400)" />
            </Box>
          }>
            <CallActivityChart trend={summary?.dailyTrend} />
          </Card>
        )}

        {widgets.topDisposition && (
          <Card title={t("cardTopDisposition")}>
            <Box sx={{ px: "20px", pb: "20px" }}>
              <Box sx={{ display: "flex", gap: "16px", alignItems: "center" }}>
                <Box sx={{ position: "relative", flexShrink: 0 }}>
                  <DonutChart segments={donutSegs} size={120} thickness={16} />
                  <Box sx={{ position: "absolute", inset: 0, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", pointerEvents: "none" }}>
                    <Typography sx={{ fontSize: "17px", fontWeight: 800, color: "var(--ink-900)", lineHeight: 1, fontFamily: "var(--font-display)", fontVariantNumeric: "tabular-nums" }}>
                      {total.toLocaleString()}
                    </Typography>
                    <Typography sx={{ fontSize: "10px", color: "var(--ink-400)", fontFamily: "var(--font-ui)" }}>{t("donutTotal")}</Typography>
                  </Box>
                </Box>
                <Box sx={{ flex: 1, display: "flex", flexDirection: "column", gap: "9px" }}>
                  {donutSegs.map(({ label, color, value }) => {
                    const pct = total ? ((value / total) * 100).toFixed(1) : 0;
                    return (
                      <Box key={label} sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: "6px" }}>
                        <Box sx={{ display: "flex", alignItems: "center", gap: "6px" }}>
                          <Box sx={{ width: 7, height: 7, borderRadius: "50%", background: color, flexShrink: 0 }} />
                          <Typography sx={{ fontSize: "11.5px", color: "var(--ink-600)", fontFamily: "var(--font-ui)" }}>{label}</Typography>
                        </Box>
                        <Typography sx={{ fontSize: "11px", color: "var(--ink-400)", fontFamily: "var(--font-ui)", fontVariantNumeric: "tabular-nums", whiteSpace: "nowrap" }}>
                          {value.toLocaleString()} ({pct}%)
                        </Typography>
                      </Box>
                    );
                  })}
                </Box>
              </Box>
            </Box>
          </Card>
        )}

        {widgets.aiInsights && (
          <Card title={t("cardAiInsights")}>
            <Box sx={{ px: "16px", pb: "16px", display: "flex", flexDirection: "column", gap: "8px" }}>
              {[
                { icon: Brain,      color: "#7A3FE4", bg: alpha("#7A3FE4", 0.10), title: t("aiCallSummary"),  sub: t("aiCallsAnalyzed", { count: answered }), nav: "analytics" },
                { icon: TrendingUp, color: "var(--success)", bg: "var(--success-bg)", title: t("aiAnswerRate"), sub: t("aiAnswerRateSub", { rate: summary?.answerRate ?? "—" }), nav: "analytics" },
                { icon: Mic,        color: "#3B7DF0", bg: alpha("#3B7DF0", 0.10), title: t("aiVoicemailAi"),  sub: t("aiVoicemailSub"), nav: "voicemail" },
              ].map(({ icon: Icon, color, bg, title, sub, nav }) => (
                <Box key={title} onClick={() => onNavigate(nav)}
                  sx={{
                    display: "flex", alignItems: "center", gap: "12px",
                    p: "12px 14px", borderRadius: "10px",
                    border: "1px solid var(--line-100)", cursor: "pointer",
                    transition: "box-shadow 0.15s, border-color 0.15s",
                    "&:hover": { boxShadow: "var(--shadow-card)", borderColor: "var(--line-200)" },
                  }}>
                  <Box sx={{ width: 34, height: 34, borderRadius: "9px", background: bg, display: "grid", placeItems: "center", flexShrink: 0 }}>
                    <Icon size={16} color={color} />
                  </Box>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography sx={{ fontSize: "13px", fontWeight: 600, color: "var(--ink-800)", fontFamily: "var(--font-ui)" }}>{title}</Typography>
                    <Typography sx={{ fontSize: "11.5px", color: "var(--ink-400)", fontFamily: "var(--font-ui)" }} noWrap>{sub}</Typography>
                  </Box>
                  <Typography sx={{ fontSize: "12px", fontWeight: 600, color: "#7A3FE4", fontFamily: "var(--font-ui)", flexShrink: 0 }}>{t("view")}</Typography>
                </Box>
              ))}
            </Box>
          </Card>
        )}
      </Box>

      {/* ── Bottom row ──────────────────────────────────────── */}
      {(widgets.recentCalls || widgets.tickets || widgets.activities) && (
        <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "1fr 1fr 1fr" }, gap: "14px" }}>

          {widgets.recentCalls && (
            <Card title={t("cardRecentCalls")} action={
              <Box component="button" onClick={() => onNavigate("callhistory")}
                sx={{ border: "none", background: "none", cursor: "pointer", color: "var(--ink-400)", display: "flex", p: 0 }}>
                <MoreHorizontal size={17} />
              </Box>
            }>
              <Box sx={{ px: "20px", pb: "16px" }}>
                {callsLoading ? (
                  [...Array(4)].map((_, i) => (
                    <Box key={i} sx={{ display: "flex", alignItems: "center", gap: "10px", py: "9px", borderBottom: "1px solid var(--line-100)", "&:last-child": { borderBottom: 0 } }}>
                      <Skeleton variant="circular" width={32} height={32} />
                      <Box sx={{ flex: 1 }}><Skeleton height={14} width="60%" /><Skeleton height={12} width="40%" /></Box>
                    </Box>
                  ))
                ) : recentCalls.length === 0 ? (
                  <Typography sx={{ fontSize: "13px", color: "var(--ink-400)", textAlign: "center", py: 2, fontFamily: "var(--font-ui)" }}>{t("noCalls")}</Typography>
                ) : (
                  recentCalls.slice(0, 4).map((c) => {
                    const cfg = CALL_STATUS_CFG[c.dialStatus || c.DialStatus] || { tkey: null, color: "#94A3B8" };
                    return (
                      <Box key={c.uniqueId || c.UniqueId} sx={{ display: "flex", alignItems: "center", gap: "10px", py: "9px", borderBottom: "1px solid var(--line-100)", "&:last-child": { borderBottom: 0 } }}>
                        <Avatar sx={{ width: 32, height: 32, fontSize: "0.7rem", fontWeight: 700, background: "var(--surface-2)", color: "var(--ink-600)", flexShrink: 0 }}>
                          {(c.callerName || c.callerId || "?")[0]?.toUpperCase()}
                        </Avatar>
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          <Typography sx={{ fontSize: "12.5px", fontWeight: 600, color: "var(--ink-900)", fontFamily: "var(--font-ui)" }} noWrap>
                            {c.callerName || c.callerId || t("unknownCaller")}
                          </Typography>
                          <Box sx={{ display: "flex", alignItems: "center", gap: "6px" }}>
                            <Chip label={cfg.tkey ? t(cfg.tkey) : "—"} size="small" sx={{ height: 16, fontSize: "0.6rem", fontWeight: 700, background: alpha(cfg.color, 0.1), color: cfg.color, border: "none", boxShadow: "none" }} />
                            <Typography sx={{ fontSize: "11px", color: "var(--ink-400)", fontFamily: "var(--font-ui)", fontVariantNumeric: "tabular-nums" }}>
                              {fmtDuration(c.duration ?? c.Duration)}
                            </Typography>
                          </Box>
                        </Box>
                        <Typography sx={{ fontSize: "11px", color: "var(--ink-400)", fontFamily: "var(--font-ui)", whiteSpace: "nowrap" }}>
                          {fmtTime(c.startTime || c.StartTime)}
                        </Typography>
                      </Box>
                    );
                  })
                )}
              </Box>
            </Card>
          )}

          {widgets.tickets && (
            <Card title={t("cardTickets")} action={
              <Box component="button" onClick={() => onNavigate("crm")}
                sx={{ border: "none", background: "none", cursor: "pointer", color: "var(--ink-400)", display: "flex", p: 0 }}>
                <MoreHorizontal size={17} />
              </Box>
            }>
              {/* Period stats bar */}
              <Box sx={{ display: "flex", alignItems: "stretch", px: "20px", pb: "12px", gap: 0 }}>
                <Box sx={{ flex: 1 }}>
                  <Typography sx={{ fontSize: "10.5px", fontWeight: 600, color: "var(--ink-400)", fontFamily: "var(--font-ui)", textTransform: "uppercase", letterSpacing: ".06em", mb: "4px" }}>
                    {t("periodTotal")}
                  </Typography>
                  {periodTickets.loading
                    ? <Skeleton width={36} height={22} />
                    : <Typography sx={{ fontSize: "22px", fontWeight: 800, color: "var(--ink-900)", fontFamily: "var(--font-display)", fontVariantNumeric: "tabular-nums", lineHeight: 1 }}>
                        {periodTickets.count ?? "—"}
                      </Typography>
                  }
                </Box>
                <Box sx={{ width: "1px", background: "var(--line-100)", mx: "16px", alignSelf: "stretch" }} />
                <Box sx={{ flex: 1 }}>
                  <Typography sx={{ fontSize: "10.5px", fontWeight: 600, color: "var(--ink-400)", fontFamily: "var(--font-ui)", textTransform: "uppercase", letterSpacing: ".06em", mb: "4px" }}>
                    {t("openNow")}
                  </Typography>
                  <Typography sx={{ fontSize: "22px", fontWeight: 800, color: "#F59E0B", fontFamily: "var(--font-display)", fontVariantNumeric: "tabular-nums", lineHeight: 1 }}>
                    {openCount ?? 0}
                  </Typography>
                </Box>
              </Box>

              <Divider sx={{ mx: "20px", mb: "4px" }} />

              {/* Open ticket list */}
              <Box sx={{ px: "20px", pb: "16px", pt: "8px" }}>
                {rawTickets.length === 0 ? (
                  <Typography sx={{ fontSize: "13px", color: "var(--ink-400)", textAlign: "center", py: 1.5, fontFamily: "var(--font-ui)" }}>{t("noOpenTickets")}</Typography>
                ) : (
                  rawTickets.slice(0, 3).map((tk) => (
                    <Box key={tk.id} onClick={() => onNavigate("crm")}
                      sx={{ display: "flex", alignItems: "center", gap: "10px", py: "8px", borderBottom: "1px solid var(--line-100)", "&:last-child": { borderBottom: 0 }, cursor: "pointer" }}>
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography sx={{ fontSize: "12px", fontWeight: 700, color: "#3B7DF0", fontFamily: "var(--font-ui)" }}>#{tk.id}</Typography>
                        <Typography sx={{ fontSize: "11.5px", color: "var(--ink-700)", fontFamily: "var(--font-ui)" }} noWrap>{tk.subject || t("noSubject")}</Typography>
                      </Box>
                      <Chip
                        label={tk.status === "open" ? t("statusOpen") : tk.status === "in_progress" ? t("statusInProgress") : tk.status}
                        size="small"
                        sx={{ height: 18, fontSize: "0.6rem", fontWeight: 700, flexShrink: 0,
                          background: tk.status === "open" ? "var(--success-bg)" : tk.status === "in_progress" ? "rgba(59,125,240,.1)" : "var(--line-100)",
                          color: tk.status === "open" ? "var(--success)" : tk.status === "in_progress" ? "#3B7DF0" : "var(--ink-500)",
                          border: "none", boxShadow: "none" }}
                      />
                    </Box>
                  ))
                )}
              </Box>
            </Card>
          )}

          {widgets.activities && (
            <Card title={t("cardActivities")} action={
              <Box sx={{ color: "var(--ink-400)" }}><MoreHorizontal size={17} /></Box>
            }>
              <Box sx={{ px: "20px", pb: "16px" }}>
                {callsLoading ? (
                  [...Array(4)].map((_, i) => (
                    <Box key={i} sx={{ display: "flex", gap: "10px", py: "9px", borderBottom: "1px solid var(--line-100)", "&:last-child": { borderBottom: 0 } }}>
                      <Skeleton variant="circular" width={28} height={28} />
                      <Box sx={{ flex: 1 }}><Skeleton height={14} width="80%" /></Box>
                    </Box>
                  ))
                ) : recentCalls.length === 0 ? (
                  <Typography sx={{ fontSize: "13px", color: "var(--ink-400)", textAlign: "center", py: 2, fontFamily: "var(--font-ui)" }}>{t("noRecentActivity")}</Typography>
                ) : (
                  recentCalls.slice(0, 4).map((c, i) => {
                    const cfg = CALL_STATUS_CFG[c.dialStatus || c.DialStatus] || { tkey: null, color: "#94A3B8" };
                    return (
                      <Box key={i} sx={{ display: "flex", alignItems: "flex-start", gap: "10px", py: "9px", borderBottom: "1px solid var(--line-100)", "&:last-child": { borderBottom: 0 } }}>
                        <Avatar sx={{ width: 28, height: 28, fontSize: "0.65rem", fontWeight: 700, background: alpha(cfg.color, 0.12), color: cfg.color, flexShrink: 0 }}>
                          {(c.callerName || "?")[0]?.toUpperCase()}
                        </Avatar>
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          <Typography sx={{ fontSize: "12.5px", color: "var(--ink-800)", fontFamily: "var(--font-ui)", lineHeight: 1.35 }}>
                            <Box component="span" sx={{ fontWeight: 600 }}>{c.callerName || c.callerId || t("unknownCaller")}</Box>
                            {" "}{cfg.tkey ? t("activityEntry", { status: t(cfg.tkey) }) : "call"}
                          </Typography>
                          <Typography sx={{ fontSize: "11px", color: "var(--ink-400)", mt: "2px", fontFamily: "var(--font-ui)" }}>
                            {fmtTime(c.startTime || c.StartTime)}
                          </Typography>
                        </Box>
                      </Box>
                    );
                  })
                )}
              </Box>
            </Card>
          )}
        </Box>
      )}

      {/* ── Date preset popover (header pill) ───────────────── */}
      <PresetMenu
        anchorEl={dateAnchor} onClose={() => setDateAnchor(null)}
        preset={preset} onPreset={applyPreset}
        customFrom={customFrom} customTo={customTo}
        onCustomFrom={setCustomFrom} onCustomTo={setCustomTo}
      />

      {/* ── Date preset popover (chart header) ──────────────── */}
      <PresetMenu
        anchorEl={chartAnchor} onClose={() => setChartAnchor(null)}
        preset={preset} onPreset={applyPreset}
        customFrom={customFrom} customTo={customTo}
        onCustomFrom={setCustomFrom} onCustomTo={setCustomTo}
      />

      {/* ── Add Widget dialog ────────────────────────────────── */}
      <Dialog open={widgetOpen} onClose={() => setWidgetOpen(false)} maxWidth="xs" fullWidth
        PaperProps={{ sx: { borderRadius: "16px" } }}>
        <DialogTitle sx={{ fontWeight: 700, fontSize: "16px", display: "flex", alignItems: "center", gap: 1, pb: 1 }}>
          <LayoutDashboard size={18} color="#7A3FE4" />
          {t("customizeDashboard")}
        </DialogTitle>
        <DialogContent sx={{ pt: 0 }}>
          <Typography sx={{ fontSize: "13px", color: "var(--ink-500)", mb: 2, fontFamily: "var(--font-ui)" }}>
            {t("customizeDesc")}
          </Typography>
          <Box sx={{ display: "flex", flexDirection: "column", gap: "2px" }}>
            {Object.entries(WIDGET_LABELS).map(([key, label]) => (
              <Box key={key} sx={{
                display: "flex", alignItems: "center", justifyContent: "space-between",
                px: "12px", py: "10px", borderRadius: "10px",
                "&:hover": { background: alpha("#7A3FE4", 0.04) },
                transition: "background 0.12s",
              }}>
                <Typography sx={{ fontSize: "14px", fontWeight: 500, color: "var(--ink-800)", fontFamily: "var(--font-ui)" }}>
                  {t(`widget${key.charAt(0).toUpperCase()}${key.slice(1)}`, { defaultValue: label })}
                </Typography>
                <Switch
                  checked={!!widgets[key]}
                  onChange={() => toggleWidget(key)}
                  size="small"
                  sx={{
                    "& .MuiSwitch-thumb": { boxShadow: "none" },
                    "& .MuiSwitch-track": { borderRadius: 6 },
                    "& .Mui-checked + .MuiSwitch-track": { background: "#7A3FE4" },
                    "& .Mui-checked .MuiSwitch-thumb": { background: "#7A3FE4" },
                  }}
                />
              </Box>
            ))}
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button onClick={() => { setWidgets(DEFAULT_WIDGETS); try { localStorage.setItem("dash_widgets", JSON.stringify(DEFAULT_WIDGETS)); } catch {} }}
            sx={{ color: "var(--ink-500)", fontFamily: "var(--font-ui)", textTransform: "none", fontSize: "13px" }}>
            {t("resetDefault")}
          </Button>
          <Button variant="contained" onClick={() => setWidgetOpen(false)}
            sx={{ background: "#7A3FE4", fontFamily: "var(--font-ui)", textTransform: "none", fontSize: "13px", fontWeight: 600, borderRadius: "8px", "&:hover": { background: "#6B2FD4" } }}>
            {t("done")}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
